import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import {
  Box,
  BoxProps,
  IconButton,
  LinearProgress,
  Modal,
} from "@mui/material";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import CompleteIcon from "../assets/Icons/CompleteIcon";
import InCompleteIcon from "../assets/Icons/InCompleteIcon";
import {
  chefOfferingFieldNames,
  publicProfileFieldNames,
} from "../constants/ProfileConstant";
import { ProfilePageAccordion, ProfilePageTab } from "../constants/ProfilePage";
import { VerifiedStatus } from "../constants/UserConstants";
import UserType from "../constants/UserType";
import { applyForVerifiedStatus, setNewChefStatus } from "../firebase/user";
import getProfileCompletenessValue from "../helper/getProfileCompletenessValue";
import { theme } from "../mui-customs/Theme";
import { RootState } from "../redux/store";
import { fetchPostJSON } from "../utils/api-helpers";
import ProfileCompletenessChart from "./profile/ProfileCompletenessChart";
import Typography from "./Typography";

const ProfileCompleteness = ({ sx, ...props }: BoxProps) => {
  const [openModal, setOpenModal] = useState(false);
  const userType = useSelector((state: RootState) => state.user.user.userType);
  const user = useSelector((state: RootState) => state.user.user);
  const { value: completenessValue, data: completenessData } =
    getProfileCompletenessValue(user);
  const userData = useSelector((state: RootState) => state.user.user);
  const router = useRouter();
  const dispatch = useDispatch();
  const hostPath = useSelector((state: RootState) => state.app.hostPath);
  const redirectUrl = `${hostPath}/profile/${userData.uid}`;
  const redirectToStripPage = async () => {
    const response = await fetchPostJSON("/api/users/create-account", {
      user: {
        uid: userData.uid,
        ...userData,
      },
      redirectUrl,
    });
    router.replace(response.data.redirectLink);
  };

  useEffect(() => {
    if (
      userData?.uid &&
      completenessValue === 100 &&
      userType === UserType.Chef &&
      userData?.verified === VerifiedStatus.False
    ) {
      applyForVerifiedStatus(userData.uid);
    }
  }, [userData, completenessValue, userType]);

  return (
    <>
      <Box
        sx={{
          cursor: "pointer",
          ...sx,
          display: `${completenessValue === 100 ? "none" : "block"}`,
        }}
        {...props}
        onClick={() => setOpenModal(true)}
      >
        <Box sx={{ display: "flex", gap: 7, mb: 2 }}>
          <Typography variant="body3" sx={{ color: theme.black[800] }}>
            Profile Completeness
          </Typography>
          <Typography variant="body3" sx={{ color: theme.black[500] }}>
            {`${completenessValue}%`}
          </Typography>
        </Box>
        <LinearProgress
          sx={{
            height: "12px",
            borderRadius: "8px",
            "& .MuiLinearProgress-bar": {
              borderRadius: "8px",
            },
          }}
          variant="determinate"
          value={completenessValue}
        />
      </Box>
      <Modal
        open={openModal}
        onClose={() => setOpenModal(false)}
        sx={{ display: "grid", placeItems: "center", outline: "none" }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            backgroundColor: theme.whiteBg.WBG1,
            width: "90%",
            maxWidth: "400px",
            height: "fit-content",
            position: "relative",
            p: 3,
            borderRadius: 1,
            textAlign: "center",
          }}
        >
          <IconButton
            sx={{ position: "absolute", left: "5%" }}
            onClick={() => setOpenModal(false)}
          >
            <ArrowBackRoundedIcon />
          </IconButton>
          <img
            src="/Global/more-pleaze-logo-green.png"
            style={{ height: "70px", width: "90px" }}
          />
          <Typography variant="h3" sx={{ color: theme.black[800] }}>
            {userType === UserType.Chef
              ? "Land your first gig!"
              : "Get the best chefs!"}
          </Typography>
          <Typography variant="body3" sx={{ color: theme.black[500] }}>
            {userType === UserType.Chef
              ? "Complete your chef profile to become a verified member of our community. Once approved, you can start bidding on gigs and showcasing your talent to clients looking for the best chefs out there. Join us and build a community of passionate chefs!"
              : "Complete your profile and get verified to receive our seasoned badge and have your gigs featured at the top of searches. This helps you connect with the best chefs out there and get the best results for your event. Thanks for joining More Pleaze!"}
          </Typography>
          <ProfileCompletenessChart setOpenModal={setOpenModal} />
        </Box>
      </Modal>
    </>
  );
};

export default ProfileCompleteness;
